import { useState } from "react";
import { ChevronDown, Download, FileText, Play, Quote, X } from "lucide-react";
import { RichText } from "@/components/RichText";
import { PdfThumbnail } from "@/components/PdfThumbnail";
import { SectionHead, SmartLink, Wrap, VideoPlayer } from "@/website/sections/Basic";

const COLS = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" };

export const CardsSection = ({ d, testId }) => (
  <Wrap testId={testId}>
    <SectionHead heading={d.heading} subheading={d.subheading} />
    <div className={`grid gap-6 ${COLS[d.columns] || COLS[3]}`}>
      {(d.items || []).map((c, i) => (
        <div key={i} className="group rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col text-slate-800" data-testid={`${testId}-card-${i}`}>
          {c.image && <div className="aspect-[4/3] overflow-hidden"><img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /></div>}
          <div className="p-5 flex-1 flex flex-col">
            <h3 className="text-lg font-bold font-['Manrope']">{c.title}</h3>
            {c.text && <RichText html={c.text} className="mt-2 text-sm text-slate-600 flex-1" />}
            {c.button_label && c.button_href && <SmartLink href={c.button_href} className="mt-4 text-sm font-semibold text-[var(--site-primary)] hover:underline" testId={`${testId}-card-${i}-link`}>{c.button_label} →</SmartLink>}
          </div>
        </div>
      ))}
    </div>
  </Wrap>
);

const Lightbox = ({ item, onClose }) => (
  <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="site-lightbox">
    <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center" onClick={onClose} data-testid="site-lightbox-close"><X className="w-5 h-5" /></button>
    <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
      {item.type === "video" ? <VideoPlayer url={item.url} /> : item.type === "pdf"
        ? <iframe src={item.url} title="pdf" className="w-full h-[85vh] rounded-lg bg-white" />
        : <img src={item.url} alt={item.caption || ""} className="max-h-[88vh] mx-auto rounded-lg" />}
      {item.caption && <p className="text-white/90 text-center mt-3">{item.caption}</p>}
    </div>
  </div>
);

export const GallerySection = ({ d, testId }) => {
  const [open, setOpen] = useState(null);
  return (
    <Wrap testId={testId}>
      <SectionHead heading={d.heading} subheading={d.subheading} />
      <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4">
        {(d.items || []).map((m, i) => (
          <button key={i} onClick={() => setOpen(m)} className="relative block w-full break-inside-avoid rounded-xl overflow-hidden group text-left" data-testid={`${testId}-item-${i}`}>
            {m.type === "pdf" ? <PdfThumbnail url={m.url} className="w-full" /> : <img src={m.thumb || m.url} alt={m.caption || ""} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
            {m.type === "video" && <span className="absolute inset-0 flex items-center justify-center"><span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center"><Play className="w-5 h-5 ml-0.5 text-slate-900" /></span></span>}
            {m.caption && <span className="absolute inset-x-0 bottom-0 p-2 text-xs text-white bg-gradient-to-t from-black/70 to-transparent">{m.caption}</span>}
          </button>
        ))}
      </div>
      {open && <Lightbox item={open} onClose={() => setOpen(null)} />}
    </Wrap>
  );
};

export const TestimonialsSection = ({ d, testId }) => (
  <Wrap testId={testId} theme="accent">
    <SectionHead heading={d.heading} subheading={d.subheading} />
    <div className="flex flex-wrap justify-center gap-6">
      {(d.items || []).map((t, i) => (
        <figure key={i} className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] rounded-2xl bg-white p-6 shadow-sm border border-slate-100 text-slate-700" data-testid={`${testId}-item-${i}`}>
          <Quote className="w-7 h-7 text-[var(--site-accent)]" />
          <blockquote className="mt-3 leading-relaxed">{t.quote}</blockquote>
          <figcaption className="mt-4 text-sm"><span className="font-bold text-slate-900">{t.name}</span>{t.role && <span className="block text-slate-500">{t.role}</span>}</figcaption>
        </figure>
      ))}
    </div>
  </Wrap>
);

export const FaqSection = ({ d, testId }) => {
  const [open, setOpen] = useState(0);
  return (
    <Wrap testId={testId} narrow>
      <SectionHead heading={d.heading} subheading={d.subheading} />
      <div className="divide-y divide-slate-200 border-y border-slate-200">
        {(d.items || []).map((f, i) => (
          <div key={i} data-testid={`${testId}-item-${i}`}>
            <button className="w-full flex items-center justify-between gap-4 py-5 text-left font-semibold text-lg" onClick={() => setOpen(open === i ? -1 : i)} data-testid={`${testId}-q-${i}`}>
              {f.question}<ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <RichText html={f.answer} className="pb-5 text-slate-600 leading-relaxed" />}
          </div>
        ))}
      </div>
    </Wrap>
  );
};

export const DownloadsSection = ({ d, testId }) => (
  <Wrap testId={testId} narrow>
    <SectionHead heading={d.heading} />
    <div className="grid sm:grid-cols-2 gap-4">
      {(d.items || []).map((f, i) => (
        <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" download className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-white hover:border-[var(--site-primary)] hover:shadow-md transition-[border-color,box-shadow]" data-testid={`${testId}-file-${i}`}>
          <FileText className="w-9 h-9 text-red-500 shrink-0" />
          <span className="flex-1 min-w-0"><span className="block font-semibold text-slate-900 truncate">{f.title}</span>{f.description && <span className="block text-sm text-slate-500 truncate">{f.description}</span>}</span>
          <Download className="w-5 h-5 text-slate-400" />
        </a>
      ))}
    </div>
  </Wrap>
);
